import React, { useState } from "react";
import { Camera, X, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Compact admin tool to attach / replace / remove a photo on a CONVICTED record.
 * Photos are restricted by the backend to records with status:
 *   Convicted | Registered | Pleaded Guilty.
 *
 * Designed to be embedded in the existing admin tooling — pasted URL only,
 * no direct uploads (keeps things simple + avoids storage costs).
 */
export default function AdminPhotoEditor({ headers }) {
  const [open, setOpen] = useState(false);
  const [abuserId, setAbuserId] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoSource, setPhotoSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const reset = () => {
    setAbuserId("");
    setPhotoUrl("");
    setPhotoSource("");
    setPreview(null);
  };

  const lookup = async () => {
    if (!abuserId.trim()) {
      toast.error("Enter a record ID first.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.get(`/abusers/${abuserId.trim()}`);
      setPreview(r.data);
      if (r.data.photo_url) setPhotoUrl(r.data.photo_url);
      if (r.data.photo_source) setPhotoSource(r.data.photo_source);
    } catch (e) {
      toast.error("Record not found.");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!preview) {
      toast.error("Look up a record first.");
      return;
    }
    if (!photoUrl.startsWith("https://")) {
      toast.error("Photo URL must start with https://");
      return;
    }
    if (!photoSource.trim()) {
      toast.error("Source attribution is required (e.g., TN TBI Registry).");
      return;
    }
    setBusy(true);
    try {
      await api.patch(
        `/admin/abusers/${preview.id}/photo`,
        { photo_url: photoUrl, photo_source: photoSource.trim() },
        { headers }
      );
      toast.success("Photo attached.");
      reset();
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!preview) return;
    if (!window.confirm("Remove the photo from this record?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/abusers/${preview.id}/photo`, { headers });
      toast.success("Photo removed.");
      reset();
      setOpen(false);
    } catch {
      toast.error("Remove failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-zinc-300 bg-white">
      <div className="bg-zinc-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera size={16} />
          <div className="font-display font-black text-sm tracking-tight">PHOTOS · CONVICTED ONLY</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-[11px] uppercase tracking-widest font-bold hover:text-red-500"
          data-testid="admin-photo-toggle"
        >
          {open ? "Close" : "Open"}
        </button>
      </div>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-xs text-zinc-600">
            Photos may only be attached to <strong>Convicted, Registered, or Pleaded Guilty</strong> records.
            Use official government sources (TN TBI, sheriff's office, DOJ press releases). Always provide
            attribution.
          </p>

          <div className="flex gap-2">
            <input
              type="text"
              value={abuserId}
              onChange={(e) => setAbuserId(e.target.value)}
              placeholder="Record ID (e.g., ny-pesek-2025)"
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm font-mono"
              data-testid="admin-photo-id"
            />
            <button
              type="button"
              onClick={lookup}
              disabled={busy}
              className="btn-outline text-xs"
              data-testid="admin-photo-lookup"
            >
              Look up
            </button>
          </div>

          {preview && (
            <div className="border border-zinc-200 p-3 bg-zinc-50 text-xs">
              <div className="font-bold">{preview.name}</div>
              <div className="text-zinc-600">
                {preview.city}, {preview.state} · {preview.status} · {preview.conviction_year}
              </div>
              {!["Convicted", "Registered", "Pleaded Guilty"].includes(preview.status) && (
                <div className="mt-1 text-amber-700">
                  ⚠ This record's status is "{preview.status}" — photo will be rejected.
                </div>
              )}
            </div>
          )}

          <input
            type="url"
            value={photoUrl}
            onChange={(e) => setPhotoUrl(e.target.value)}
            placeholder="https://… (HTTPS photo URL)"
            className="w-full border border-zinc-300 px-3 py-2 text-sm"
            data-testid="admin-photo-url"
          />

          <input
            type="text"
            value={photoSource}
            onChange={(e) => setPhotoSource(e.target.value)}
            placeholder='Source attribution (e.g., "TN TBI Registry")'
            className="w-full border border-zinc-300 px-3 py-2 text-sm"
            data-testid="admin-photo-source"
          />

          {photoUrl.startsWith("https://") && (
            <div className="border border-zinc-200 p-2 bg-zinc-50">
              <div className="label-eyebrow mb-1">Preview</div>
              <img
                src={photoUrl}
                alt="preview"
                className="max-h-32 border border-zinc-300"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={busy || !preview}
              className="inline-flex items-center gap-1.5 bg-zinc-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-600"
              data-testid="admin-photo-save"
            >
              <ImageIcon size={13} /> {busy ? "Saving…" : "Attach photo"}
            </button>
            {preview?.photo_url && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 border border-red-600 text-red-700 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-600 hover:text-white"
                data-testid="admin-photo-remove"
              >
                <Trash2 size={13} /> Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
